import React, { useState } from 'react';
import { Users, Key, Mail, User, Briefcase, MessageSquare, Send } from 'lucide-react';
import { api } from '../../lib/api';

interface JoinOrganizationFormProps {
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  onSubmit: (data: {
    joiningCode: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    jobTitle?: string;
    message?: string;
  }) => void;
  isLoading: boolean;
}

export default function JoinOrganizationForm({ 
  userEmail, 
  userFirstName,
  userLastName,
  onSubmit, 
  isLoading 
}: JoinOrganizationFormProps) {
  const [formData, setFormData] = useState({
    joiningCode: '',
    firstName: userFirstName,
    lastName: userLastName,
    preferredName: '',
    email: userEmail,
    jobTitle: '',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [organizationInfo, setOrganizationInfo] = useState<{
    isValid: boolean;
    organizationName?: string;
  } | null>(null);

  const validateJoiningCode = async (code: string) => {
    if (!code.trim()) {
      setOrganizationInfo(null);
      return;
    }

    // Remove any spaces and format as XXXX-XXXX-XXXX-XXXX
    const cleanCode = code.replace(/\s/g, '').toUpperCase();
    if (cleanCode.length === 16) {
      const formattedCode = cleanCode.replace(/(.{4})/g, '$1-').slice(0, -1);
      setFormData(prev => ({ ...prev, joiningCode: formattedCode }));
    }

    try {
      const response = await api.post('/organizations/validate-joining-code', {
        joiningCode: code.trim()
      });

      setOrganizationInfo({
        isValid: true,
        organizationName: response.data.data.organizationName
      });
    } catch (error) {
      console.error('Error validating joining code:', error);
      setOrganizationInfo({
        isValid: false
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.joiningCode.trim()) {
      newErrors.joiningCode = 'Joining code is required';
    } else if (!/^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(formData.joiningCode.trim())) {
      newErrors.joiningCode = 'Please enter a valid joining code format (XXXX-XXXX-XXXX-XXXX)';
    } else if (!organizationInfo?.isValid) {
      newErrors.joiningCode = 'This joining code does not match any existing organization';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        joiningCode: formData.joiningCode.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        preferredName: formData.preferredName.trim() || undefined,
        email: formData.email.trim(),
        jobTitle: formData.jobTitle.trim() || undefined,
        message: formData.message.trim() || undefined,
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleJoiningCodeChange = (value: string) => {
    // Allow pasting of code with or without dashes
    let cleanValue = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    // Limit to 16 characters
    if (cleanValue.length > 16) {
      cleanValue = cleanValue.substring(0, 16);
    }
    
    // Format with dashes
    let formattedValue = '';
    for (let i = 0; i < cleanValue.length; i += 4) {
      if (i > 0) formattedValue += '-';
      formattedValue += cleanValue.substring(i, i + 4);
    }
    
    setFormData(prev => ({ ...prev, joiningCode: formattedValue }));
    
    if (errors.joiningCode) {
      setErrors(prev => ({ ...prev, joiningCode: '' }));
    }
    
    // Validate when we have a complete code
    if (cleanValue.length === 16) {
      const timeoutId = setTimeout(() => {
        validateJoiningCode(formattedValue);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setOrganizationInfo(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center mx-auto mb-3">
          <Users className="h-6 w-6 text-blue-600" />
        </div>
        <p className="text-muted-foreground">
          Enter your organization's joining code and your details to request access.
        </p>
      </div>

      {/* Joining Code */}
      <div>
        <label htmlFor="joiningCode" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Key className="h-4 w-4" />
          Organization Joining Code <span className="text-red-500">*</span>
        </label>
        <input
          id="joiningCode"
          type="text"
          value={formData.joiningCode}
          onChange={(e) => handleJoiningCodeChange(e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-lg tracking-wider ${
            errors.joiningCode ? 'border-red-500' : 'border-input'
          }`}
          placeholder="XXXX-XXXX-XXXX-XXXX"
          disabled={isLoading}
        />
        {errors.joiningCode && (
          <p className="text-sm text-red-500 mt-1">{errors.joiningCode}</p>
        )}
        
        {/* Organization Validation Feedback */}
        {organizationInfo && (
          <div className={`mt-2 p-3 rounded-md ${
            organizationInfo.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            {organizationInfo.isValid ? (
              <div className="flex items-center gap-2 text-green-800">
                <Users className="h-4 w-4" />
                <div>
                  <p className="font-medium">Organization found!</p>
                  <p className="text-sm">You're requesting to join: {organizationInfo.organizationName}</p>
                </div>
              </div>
            ) : (
              <p className="text-red-800 text-sm">This joining code doesn't match any organization</p>
            )}
          </div>
        )}
        
        <p className="text-sm text-muted-foreground mt-1">
          Get this code from your organization administrator
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.firstName ? 'border-red-500' : 'border-input'
            }`}
            placeholder="Enter your first name"
            disabled={isLoading}
          />
          {errors.firstName && (
            <p className="text-sm text-red-500 mt-1">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.lastName ? 'border-red-500' : 'border-input'
            }`}
            placeholder="Enter your last name"
            disabled={isLoading}
          />
          {errors.lastName && (
            <p className="text-sm text-red-500 mt-1">{errors.lastName}</p>
          )}
        </div>
      </div>

      {/* Preferred Name */}
      <div>
        <label htmlFor="preferredName" className="block text-sm font-medium mb-1">
          Preferred Name (Optional)
        </label>
        <input
          id="preferredName"
          type="text"
          value={formData.preferredName}
          onChange={(e) => handleInputChange('preferredName', e.target.value)}
          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="What would you like to be called?"
          disabled={isLoading}
        />
      </div>

      {/* Email Address */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 ${
            errors.email ? 'border-red-500' : 'border-input'
          }`}
          placeholder="your.email@example.com"
          disabled={true} // Always disabled as it comes from Clerk
        />
        {errors.email && (
          <p className="text-sm text-red-500 mt-1">{errors.email}</p>
        )}
        <p className="text-sm text-muted-foreground mt-1">
          This email is linked to your login account
        </p>
      </div>

      {/* Job Title */}
      <div>
        <label htmlFor="jobTitle" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          Job Title (Optional)
        </label>
        <input
          id="jobTitle"
          type="text"
          value={formData.jobTitle}
          onChange={(e) => handleInputChange('jobTitle', e.target.value)}
          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="e.g., Marketing Manager, Software Developer"
          disabled={isLoading}
        />
        <p className="text-sm text-muted-foreground mt-1">
          Your administrator may update this after approval
        </p>
      </div>

      {/* Message for Admins */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Message for Administrators (Optional)
        </label>
        <textarea
          id="message"
          value={formData.message}
          onChange={(e) => handleInputChange('message', e.target.value)}
          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          placeholder="Tell the administrators why you'd like to join or any additional context..."
          rows={3}
          disabled={isLoading}
        />
        <p className="text-sm text-muted-foreground mt-1">
          This message will be included in the email sent to organization administrators
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={isLoading || !organizationInfo?.isValid}
          className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4" />
          Request Access
        </button>
      </div>

      {/* Info about what happens next */}
      {organizationInfo?.isValid && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Your request will be sent to {organizationInfo.organizationName} administrators</li>
            <li>• They will review your request and can approve or reject it</li>
            <li>• You'll receive an email notification with their decision</li>
            <li>• If approved, you'll gain access to the organization's Nexus workspace</li>
          </ul>
        </div>
      )}
    </form>
  );
}
